/**
 * @fileoverview Custom hook for checking if a template has arbitration documents.
 */

import {useQuery} from "react-query";
import {http} from "../../services/api";

type TemplateMetadataResponse = {
  hasArbitrationDocument: boolean;
};

/**
 * Custom hook for checking if a template has arbitration documents.
 * Relies solely on backend metadata endpoint response.
 *
 * @param {string} facilityId The facility ID.
 * @param {string} templateName The template name.
 * @param {Object} options Query options.
 * @param {boolean} options.enabled Whether the query should be enabled.
 * @returns {Object} React Query result containing hasArbitrationDocument boolean.
 */
export const useTemplateHasArbitrationDocumentQuery = (
  facilityId: string | undefined,
  templateName: string | undefined,
  {enabled = true}: {enabled?: boolean} = {},
) =>
  useQuery<{hasArbitrationDocument: boolean}>(
    ["templateHasArbitrationDocument", facilityId, templateName],
    async () => {
      try {
        const metadataResponse = await http.get<TemplateMetadataResponse>(
          `/v3/documents/template/${facilityId}/${templateName}/metadata`,
        );

        if (metadataResponse.ok && metadataResponse.data) {
          return {
            hasArbitrationDocument:
              metadataResponse.data.hasArbitrationDocument ?? false,
          };
        }

        // If endpoint doesn't exist or fails, return false
        return {hasArbitrationDocument: false};
      } catch (error: any) {
        // If endpoint doesn't exist (404) or other error, return false
        return {hasArbitrationDocument: false};
      }
    },
    {
      enabled: Boolean(enabled && facilityId && templateName),
      // Return false by default if query is disabled or fails
      // This allows components to show the option and let backend handle 404s
      retry: false,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  );
